/**
 * ==========================================
 * PUSHER PROVIDER
 * ==========================================
 * File: frontend/src/shared/realtime/PusherProvider.tsx
 *
 * React context provider for Pusher real-time connection.
 * Initializes PusherManager on mount at the app level,
 * ensuring Pusher is ready before any component tries to subscribe.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { getPusherManager } from '../../modules/session/services/pusher_manager';
import { pusherApi } from '../../modules/session/services/live_sessions_api';

// Get the type from the return value of getPusherManager
type PusherManager = ReturnType<typeof getPusherManager>;


// ============================================
// CONTEXT
// ============================================

interface PusherContextValue {
  /** The PusherManager instance */
  manager: PusherManager;
  /** Whether Pusher has been initialized */
  isInitialized: boolean;
  /** Whether initialization is in progress */
  isInitializing: boolean;
  /** Error message if initialization failed */
  error: string | null;
  /** Manually trigger initialization (useful after login) */
  initialize: () => Promise<boolean>;
}

const PusherContext = createContext<PusherContextValue | null>(null);

// ============================================
// PROVIDER
// ============================================

interface PusherProviderProps {
  children: React.ReactNode;
  /** Whether to auto-initialize on mount (default: true) */
  autoInitialize?: boolean;
}

export function PusherProvider({ children, autoInitialize = true }: PusherProviderProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [initAttempted, setInitAttempted] = useState(false);

  const manager = getPusherManager();

  const initialize = useCallback(async (): Promise<boolean> => {
    // Already initialized
    if (manager.isInitialized()) {
      setIsInitialized(true);
      return true;
    }

    // Already initializing or already attempted
    if (isInitializing) {
      return false;
    }

    setIsInitializing(true);
    setError(null);

    try {
      const config = await pusherApi.getConfig();
      const success = manager.initialize(config);

      if (success) {
        setIsInitialized(true);
        return true;
      } else {
        setError('Pusher configuration not available');
        console.warn('[PusherProvider] Pusher not configured');
        return false;
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to initialize Pusher';
      setError(message);
      console.error('[PusherProvider] Failed to initialize Pusher:', err);
      return false;
    } finally {
      setIsInitializing(false);
      setInitAttempted(true);
    }
  }, [manager, isInitializing]);

  // Auto-initialize on mount ONCE
  useEffect(() => {
    // Skip if already attempted, already initialized, or auto-init disabled
    if (initAttempted || isInitialized || !autoInitialize) {
      return;
    }

    // Check if manager is already initialized (from previous mount)
    if (manager.isInitialized()) {
      setIsInitialized(true);
      setInitAttempted(true);
      return;
    }

    initialize();
  }, [autoInitialize, manager, initialize, initAttempted, isInitialized]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Note: We don't disconnect here because the manager is a singleton
      // and might be used by other parts of the app
    };
  }, []);

  const value: PusherContextValue = {
    manager,
    isInitialized,
    isInitializing,
    error,
    initialize
  };

  return (
    <PusherContext.Provider value={value}>
      {children}
    </PusherContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

/**
 * Hook to access Pusher context.
 * Must be used within a PusherProvider.
 */
export function usePusher(): PusherContextValue {
  const context = useContext(PusherContext);
  if (!context) {
    throw new Error('usePusher must be used within a PusherProvider');
  }
  return context;
}

/**
 * Hook that returns true only when Pusher is ready for subscriptions.
 * Useful for conditionally enabling channel subscriptions.
 */
export function usePusherReady(): boolean {
  const { isInitialized } = usePusher();
  return isInitialized;
}

export default PusherProvider;
